import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface CaptionCellProps extends HTMLAttributes<HTMLSpanElement> {
  /** The caption row's content (a task's title, a label). */
  caption: ReactNode;
  /** Attributes for the caption row itself (a `data-part`, a class). */
  captionProps?: HTMLAttributes<HTMLSpanElement> &
    Record<`data-${string}`, string | undefined>;
}

/**
 * The button's two-row cell (Sebastian, 2026-10-20: "task progress and task
 * bar can be paired better UI-wise: share a pill or a more distinct element
 * — still two rows, but a shared component"): ONE surface holding a caption
 * row and a body row — the task's title over its progress bar. The caption
 * is the darker band of the same surface; the body is the caller's (the
 * bar, its fill and count), positioned inside `.igw-caption-cell__body`.
 * Surface, size and type are the caller's through `className`; the rows'
 * layout and the band are `.igw-caption-cell` in `igw-button.css`.
 */
export const CaptionCell = forwardRef<HTMLSpanElement, CaptionCellProps>(
  function CaptionCell(
    { caption, captionProps, className, children, ...rest },
    ref
  ) {
    const { className: captionClass, ...captionRest } = captionProps ?? {};
    return (
      <span ref={ref} className={cn("igw-caption-cell", className)} {...rest}>
        <span
          {...captionRest}
          className={cn("igw-caption-cell__caption", captionClass)}
        >
          {caption}
        </span>
        <span className="igw-caption-cell__body">{children}</span>
      </span>
    );
  }
);
